import { createLocalUrl, isInternetExplorer } from './util'
import { AccountManager } from './msalAuth'
import { FallbackError } from './errors'
import { captureException } from '../../sentry-config'

type DialogEventMessage = { message: string; origin: string | undefined }
type DialogEventError = { error: number }
type DialogEventArg = DialogEventMessage | DialogEventError

export type AuthDialogResult = {
  accessToken?: string
  error?: string
}

export enum AuthMethod {
  NAA, // Edge webview (Nested App Auth).
  MSALV2DialogApi, // For Trident IE11 webview support.
}

let msalAccountManager: AccountManager | undefined // Account manager for MSAL @latest (NAA). Imported dynamically only if needed.
let authMethod = AuthMethod.NAA // Default to nested app authentication.
let dialogApiResult: Promise<string> | null = null

/**
 * Initializes which authentication method to use based on which webview is in use (Edge or Trident IE11.)
 */
export async function initializeAuthMethod() {
  // Check if Trident IE11 webview is in use.
  if (isInternetExplorer()) {
    // Fall back to MSAL v2 to support Trident IE11 webview.
    authMethod = AuthMethod.MSALV2DialogApi
  } else {
    // Initialize the MSAL @latest (NAA) library.
    // Dynamically import the auth config code (MSAL @latest won't load in Trident IE11 webview.)
    const accountModule = await import('./msalAuth')
    msalAccountManager = new accountModule.AccountManager()
    await msalAccountManager.initialize()
  }
}

function getSignInDialogUrl() {
  if (authMethod === AuthMethod.MSALV2DialogApi) {
    return createLocalUrl('dialoginternetexplorer.html')
  } else {
    return createLocalUrl('dialog.html')
  }
}

/**
 * Gets an access token for requested scopes. Handles switching if fallback auth is in use.
 */
export async function getAccessToken(scopes: string[]): Promise<string> {
  switch (authMethod) {
    case AuthMethod.NAA:
      return getTokenWithMsal(scopes)
    case AuthMethod.MSALV2DialogApi:
      // If Trident IE11 webview is in use, call getUserProfileWithDialogApi() to use the MSAL v2 compatible library.
      return getTokenWithDialogApi()
  }
}

async function getTokenWithMsal(scopes: string[]): Promise<string> {
  if (dialogApiResult) {
    return dialogApiResult
  }
  if (msalAccountManager === undefined) {
    const error = new Error('msalAccountManager was not initialized!')
    captureException(error)
    throw error
  }

  let token = ''

  try {
    token = await msalAccountManager.ssoGetAccessToken(scopes)
  } catch (ex) {
    if (ex instanceof FallbackError) {
      token = await getTokenWithDialogApi()
    } else {
      captureException(ex)
      throw ex
    }
  }

  return token
}
/**
 * Uses the Office Dialog API to open an MSAL v2 auth window to sign in the user.
 * Used for Trident IE11 webview compatibility.
 * @returns The access token for the signed in user.
 */
export async function getTokenWithDialogApi(): Promise<string> {
  if (dialogApiResult) {
    return dialogApiResult
  }

  dialogApiResult = new Promise((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      getSignInDialogUrl(),
      { height: 60, width: 30 },
      result => {
        result.value.addEventHandler(
          Office.EventType.DialogEventReceived,
          (arg: DialogEventArg) => {
            const errorArg = arg as DialogEventError
            if (errorArg.error == 12006) {
              dialogApiResult = null
              reject('Dialog closed')
            }
          }
        )
        result.value.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (arg: DialogEventArg) => {
            const messageArg = arg as DialogEventMessage
            const parsedMessage = JSON.parse(messageArg.message)
            result.value.close()

            if (parsedMessage.error) {
              captureException(new Error(`Dialog message error: ${parsedMessage.error}`))
              reject(parsedMessage.error)
              dialogApiResult = null
            } else {
              resolve(parsedMessage.accessToken)
            }
          }
        )
      }
    )
  })
  return dialogApiResult
}
